import type { DateRangePreset } from "../../Files/helpers/date-range-filter.ts"

/** A company-scoped file has neither; a Project/Lead file is exactly one of
 * the other two. Mirrors the backend's own `?location=` values verbatim
 * (`ProjectAttachmentViewSet`/`AttachmentViewSet.get_queryset`). */
type GlobalFilesLocation = "company" | "project" | "lead"

const GLOBAL_FILES_LOCATIONS: GlobalFilesLocation[] = ["company", "project", "lead"]

/** Mirrors the backend's own `?attention=` values verbatim
 * (`apps.project_management.document_health`) — the same keys
 * `GlobalFilesOverviewView`'s Document Health breakdown rows carry, so a
 * row's `key` can be passed straight through as this filter's value. */
type GlobalFilesAttention =
  | "needs_attention"
  | "awaiting_signature"
  | "review_overdue"
  | "missing_document_type"
  | "changes_requested"

const GLOBAL_FILES_ATTENTION_CATEGORIES: GlobalFilesAttention[] = [
  "awaiting_signature",
  "review_overdue",
  "missing_document_type",
  "changes_requested"
]

/** Every dimension here is backed by a real, already-working server param —
 * see `docs/kb` for the ones deliberately left out (File Type has no
 * server-side physical-format filter on either store; Uploaded By has no
 * company-wide user search endpoint to reuse). */
interface GlobalFilesFilters {
  location: GlobalFilesLocation | null
  // Meaningful only alongside `location: "project"` or `"lead"` — a specific
  // Project/Lead id narrowing that location further. Label is captured at
  // pick time (no static list resolves an id back to one).
  recordId: string | null
  recordLabel: string | null
  documentType: string | null
  documentTypeLabel: string | null
  status: string | null
  statusLabel: string | null
  // The "uploaded by this specific customer" filter — mirrors the per-
  // project Files drawer's Origin/Customer control exactly (same backend
  // method, `filter_by_creator_customer`), not "files belonging to a
  // customer's project".
  customerIds: string[]
  customerLabels: string[]
  dateRange: DateRangePreset | null
  dateAfter: string | null
  dateBefore: string | null
  // Document Health drill-through — Overview's "Need Attention" KPI and its
  // per-row "View all" both land here via this same field.
  attention: GlobalFilesAttention | null
}

const EMPTY_GLOBAL_FILES_FILTER_STATE: GlobalFilesFilters = {
  location: null,
  recordId: null,
  recordLabel: null,
  documentType: null,
  documentTypeLabel: null,
  status: null,
  statusLabel: null,
  customerIds: [],
  customerLabels: [],
  dateRange: null,
  dateAfter: null,
  dateBefore: null,
  attention: null
}

const countActiveGlobalFilesFilters = (filters: GlobalFilesFilters): number => {
  let count = 0
  if (filters.location) count += 1
  // `recordId` narrows `location` further rather than standing as its own
  // dimension — it never counts (or chips) on its own.
  if (filters.documentType) count += 1
  if (filters.status) count += 1
  if (filters.customerIds.length > 0) count += 1
  if (filters.dateRange) count += 1
  if (filters.attention) count += 1
  return count
}

export {
  countActiveGlobalFilesFilters,
  EMPTY_GLOBAL_FILES_FILTER_STATE,
  GLOBAL_FILES_ATTENTION_CATEGORIES,
  GLOBAL_FILES_LOCATIONS
}
export type { GlobalFilesAttention, GlobalFilesFilters, GlobalFilesLocation }
